import { Track } from "@/src/lib/tracking";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { homeQueryKeys } from "@/src/services/homeQueryKeys";
import {
  OpenDataTypeEnum,
  ThemeItem,
  ThemeItemPaginationResponse,
} from "@features/api/openData";
import { HeatmapNode } from "@components/Heatmap/types";
import type { StockType } from "@utils/stockType";
import { ThemesScopeEnum } from "@const/market";
import { I18nUtil } from "@utils/i18n";
interface UseThemesHeatmapDataOptions {
  /** 是否启用请求 */
  enabled?: boolean;
  /** 股票市场，仅 type 为 stock 时生效（US/HK） */
  stockType?: StockType;
}

/**
 * 获取热力图所需主题数据的 Hook
 * - 使用 themes/ranking 接口，按 types=["heatmap"] 拉取所有主题
 * - 一次性循环请求到没有数据为止
 * - 获取到的数据按 MC (market_cap) 降序排序
 */
export const useThemesHeatmapData = (
  type: "crypto" | "stock",
  options: UseThemesHeatmapDataOptions = {},
) => {
  const { enabled = true, stockType } = options;
  const { openDataApi } = useOspApiClientProvider();
  const locale = I18nUtil.getCurrentLocale();

  const dataType =
    type === "crypto" ? OpenDataTypeEnum.TOKEN : OpenDataTypeEnum.STOCK;

  // 使用 themes/ranking 按 types=["heatmap"] 拉取所有主题
  const result = useQuery({
    queryKey: homeQueryKeys.themesHeatmap(
      type,
      stockType,
      (locale as string) || "en",
    ),
    queryFn: async (): Promise<ThemeItem[]> => {
      if (!openDataApi?.themes?.getRankingThemes) {
        throw new Error("Themes ranking API not available");
      }

      try {
        const allThemes: ThemeItem[] = [];
        const limit = 20;
        let offset = 0;

        // 循环请求直至没有更多数据
        // 注意：offset/limit 语义参考 GetRankingThemeRequest
        for (;;) {
          const response = await openDataApi.themes.getRankingThemes({
            offset,
            limit,
            type: dataType,
            scope: ThemesScopeEnum.HeatMap,
            // stock 场景下区分 US/HK 市场
            market: type === "stock" ? stockType : undefined,
            language: I18nUtil.getBackgroundLanguageKey(
              (locale as string) || "en",
            ),
            "enrich.enable": true,
            "enrich.with_market.enable": true,
            "enrich.with_symbol.enable": false,
          });

          const paginationData = (response?.data as ThemeItemPaginationResponse)
            ?.data;
          const rows = paginationData?.rows ?? [];

          if (!rows.length) {
            Track.log(
              "info",
              "useThemesHeatmapData",
              `offset=${offset} 返回 0 条数据，停止请求`,
            );
            break;
          }

          allThemes.push(...rows);

          if (rows.length < limit) {
            Track.log(
              "info",
              "useThemesHeatmapData",
              `rows.length < limit(${limit})，认为已到末尾，停止请求`,
            );
            break;
          }

          offset += limit;
        }

        Track.log(
          "info",
          "useThemesHeatmapData",
          `themes/ranking 所有数据获取完成，共 ${allThemes.length} 个主题`,
        );

        // 按 market_cap 降序排序
        const sortedThemes = allThemes.sort((a, b) => {
          const mcA = Number(a.market_data?.market_cap ?? 0);
          const mcB = Number(b.market_data?.market_cap ?? 0);
          return mcB - mcA;
        });

        Track.log("info", "useThemesHeatmapData", `数据排序完成，按 MC 降序`);

        return sortedThemes;
      } catch (error) {
        Track.error(error, {
          tag: "useThemesHeatmapData",
          msg: "获取主题数据失败:",
        });
        throw error;
      }
    },
    enabled: enabled && !!openDataApi,
    staleTime: 30 * 1000, // 30秒内不重新请求
  });

  // 将 ThemeItem 转换为 HeatmapNode，保持 MC 排序顺序
  const heatmapNodes: HeatmapNode[] = useMemo(() => {
    if (!result.data || result.data.length === 0) {
      return [];
    }

    // 转换为 HeatmapNode，过滤掉没有 theme 的项
    // 数据已经按 MC 降序排序，保持原有顺序
    return result.data
      .filter((item): item is ThemeItem & { theme: string } => !!item.theme)
      .map((item) => {
        const marketCapStr = item.market_data?.market_cap;
        const percentageStr =
          item.market_data?.price_change_percentage?.percentage;

        const marketCap = marketCapStr ? Number(marketCapStr) : null;
        const changePercent24h = percentageStr ? Number(percentageStr) : null;

        return {
          id: `${item.type}-${item.theme}`,
          name: item.theme,
          marketCap: Number.isNaN(marketCap) ? null : marketCap,
          changePercent24h: Number.isNaN(changePercent24h)
            ? null
            : changePercent24h,
          meta: item,
        };
      });
  }, [result.data]);

  return {
    ...result,
    data: heatmapNodes,
  };
};
